/**
 * The saved cache (D-87): «Бүгүн» opens from the last loaded day, even with no
 * signal, and refreshes in the background.
 *
 * Saved on the device, in this WebView's local storage: who is signed in and
 * the screen data in SAVED_KEYS, for at most MAX_AGE_MS. Nothing else. It is
 * wiped at «Выйти» and whenever the server says the session is gone (401).
 *
 * Our own persister rather than @tanstack/query-sync-storage-persister: that
 * one saves on a delay, so a save queued just before sign-out could write the
 * data back after the wipe. Here forgetSavedData() also cancels it.
 */
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import type { Query, QueryClient } from '@tanstack/react-query';

export const STORAGE_KEY = 'masterday-cache';
export const MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** Bump the number when a saved screen's data changes shape. */
export const BUSTER = `${__APP_VERSION__}:1`;
/** First part of each query key that is saved. Everything else stays in memory only. */
export const SAVED_KEYS = ['me', 'today', 'week', 'inbox', 'unread', 'clients'] as const;
const SAVE_DELAY_MS = 1000;

/**
 * Anything in SAVED_KEYS that holds data. A refresh that failed offline keeps
 * its last data and stays saved; requiring 'success' here erased the saved day
 * after one offline launch (found 2026-10-06).
 */
export function shouldSave(query: Pick<Query, 'queryKey' | 'state'>): boolean {
  return (SAVED_KEYS as readonly unknown[]).includes(query.queryKey[0]) && query.state.data != null;
}

/** Saved queries must outlive the default 5-minute garbage collection, or they vanish before use. */
export function keepSavedQueries(client: QueryClient): void {
  for (const key of SAVED_KEYS) client.setQueryDefaults([key], { gcTime: MAX_AGE_MS });
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function createPersister(): Persister & { flush(): void } {
  let pending: PersistedClient | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const flush = () => {
    clearTimeout(timer);
    timer = undefined;
    const client = pending;
    pending = null;
    if (!client) return;
    try {
      if (client.clientState.queries.length) storage()?.setItem(STORAGE_KEY, JSON.stringify(client));
      else storage()?.removeItem(STORAGE_KEY);
    } catch {
      // Full or blocked storage: the app still works, it just opens without saved data.
    }
  };

  return {
    persistClient(client) {
      pending = client;
      if (timer === undefined) timer = setTimeout(flush, SAVE_DELAY_MS);
    },
    restoreClient() {
      try {
        const raw = storage()?.getItem(STORAGE_KEY);
        return raw ? (JSON.parse(raw) as PersistedClient) : undefined;
      } catch {
        return undefined;
      }
    },
    removeClient() {
      clearTimeout(timer);
      timer = undefined;
      pending = null;
      try {
        storage()?.removeItem(STORAGE_KEY);
      } catch {
        // nothing saved to remove
      }
    },
    flush,
  };
}

export const persister = createPersister();

/** Save at once when the app goes to the background, where it may be closed. */
export function saveOnBackground(): void {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') persister.flush();
  });
}

/** «Выйти», or the server said the session is gone: nothing of this account stays on the device. */
export function forgetSavedData(): void {
  void persister.removeClient();
}
